import * as React from "react";
import { DefaultError, QueryClient, QueryKey } from "@tanstack/query-core";
import {
  DefinedUseQueryResult,
  DefinedInitialDataOptions,
  UndefinedInitialDataOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { getHooksForQueryKey } from "./queryHookRegistry";

export function useHookedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & {
    queryKey: TQueryKey;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError>;
export function useHookedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & {
    queryKey: TQueryKey;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError>;
export function useHookedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
    queryKey: TQueryKey;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError>;
export function useHookedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options:
    | (DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & {
      queryKey: TQueryKey;
    })
    | (UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey> & {
      queryKey: TQueryKey;
    })
    | (UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & {
      queryKey: TQueryKey;
    }),
  queryClient?: QueryClient
): UseQueryResult<TData, TError> {
  const queryResult = useQuery(options, queryClient);
  const registeredHooks = getHooksForQueryKey<TData, TError>(options.queryKey);

  return React.useMemo(() => {
    return registeredHooks.reduce<UseQueryResult<TData, TError>>((currentResult, registration) => {
      return registration.hook(currentResult);
    }, queryResult);
  }, [ queryResult, registeredHooks ]);
}
